/**
 * Google Analytics 4 (GA4) integration module for KOMBU
 * 
 * This module provides type-safe event tracking for the customer journey:
 * - Page views (automatic via AnalyticsProvider)
 * - Funnel events: identify → memory → video → share
 * 
 * Requires VITE_GA_MEASUREMENT_ID environment variable to be set
 * @see https://developers.google.com/analytics/devguides/collection/ga4
 */

// GA4 Measurement ID from environment variable
export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

// Initialize dataLayer immediately (before script loads)
// This ensures dataLayer is available for debugging
if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
}

// Extend Window interface to include gtag
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

// Track whether GA is fully initialized (script loaded + config called)
let isGAInitialized = false;

/**
 * Check if GA4 is enabled (has measurement ID configured)
 */
export function isGAEnabled(): boolean {
  return !!GA_MEASUREMENT_ID;
}

/**
 * Type-safe event names for KOMBU analytics
 */
export type AnalyticsEventName =
  // Page navigation
  | 'page_view'
  // QR scan
  | 'qr_scan'
  // Identify flow
  | 'identify_start'
  | 'identify_submit'
  | 'identify_error'
  // Memory flow
  | 'memory_view'
  | 'memory_error'
  // Photo upload
  | 'photo_upload_start'
  | 'photo_upload_success'
  | 'photo_upload_error'
  // Ratings
  | 'rating_submit'
  // Review
  | 'review_submit'
  // Video events
  | 'video_ready'
  | 'video_play'
  | 'video_pause'
  | 'video_progress'
  | 'video_complete'
  | 'video_error'
  // Share/Download
  | 'share_click'
  | 'share_success'
  | 'share_error'
  | 'download_click'
  | 'download_success'
  | 'download_error'
  // Admin - Auth
  | 'admin_login_success'
  | 'admin_login_failure'
  | 'admin_logout'
  // Admin - Orders
  | 'admin_order_view'
  | 'admin_order_print_thermal'
  | 'admin_order_print_browser'
  // Admin - Items
  | 'admin_item_create'
  | 'admin_item_edit'
  | 'admin_item_delete'
  // Admin - Multimedia
  | 'admin_chef_video_upload'
  | 'admin_chef_video_delete'
  | 'admin_ambient_photo_upload'
  | 'admin_ambient_photo_delete'
  // Admin - Settings
  | 'admin_settings_save';

/**
 * Event parameters by event type
 */
export interface AnalyticsEventParams {
  page_view: { page_path: string; page_title?: string };
  qr_scan: { order_id: string; restaurant?: string };
  identify_start: { order_id: string; restaurant?: string };
  identify_submit: { order_id: string; restaurant?: string };
  identify_error: { order_id: string; error_message: string };
  memory_view: { memory_slug: string; status: string; restaurant?: string };
  memory_error: { memory_slug: string; error_message: string };
  photo_upload_start: { memory_slug: string };
  photo_upload_success: { memory_slug: string };
  photo_upload_error: { memory_slug: string; error_message: string };
  rating_submit: { memory_slug: string; item_id: string; rating: number };
  review_submit: { memory_slug: string };
  video_ready: { memory_slug: string; restaurant?: string };
  video_play: { memory_slug: string; restaurant?: string };
  video_pause: { memory_slug: string; current_time?: number };
  video_progress: { memory_slug: string; percent: number };
  video_complete: { memory_slug: string; restaurant?: string };
  video_error: { memory_slug: string; error_message: string };
  share_click: { memory_slug: string; method?: 'native' | 'fallback' };
  share_success: { memory_slug: string; method?: 'file' | 'link' };
  share_error: { memory_slug: string; error_message: string };
  download_click: { memory_slug: string };
  download_success: { memory_slug: string };
  download_error: { memory_slug: string; error_message: string };
  admin_login_success: { restaurant_id?: string };
  admin_login_failure: { error_message: string };
  admin_logout: { restaurant_id?: string };
  admin_order_view: { order_id: string; restaurant_id?: string };
  admin_order_print_thermal: { order_id: string; restaurant_id?: string };
  admin_order_print_browser: { order_id: string; restaurant_id?: string };
  admin_item_create: { item_id: string; restaurant_id?: string };
  admin_item_edit: { item_id: string; restaurant_id?: string };
  admin_item_delete: { item_id: string; restaurant_id?: string };
  admin_chef_video_upload: { restaurant_id?: string };
  admin_chef_video_delete: { restaurant_id?: string };
  admin_ambient_photo_upload: { restaurant_id?: string };
  admin_ambient_photo_delete: { photo_id: string; restaurant_id?: string };
  admin_settings_save: { restaurant_id?: string };
}

/**
 * Check if gtag is available
 */
export function isGtagAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/**
 * Check if GA is fully initialized (script loaded and config called)
 */
export function isGAReady(): boolean {
  return isGAInitialized && isGtagAvailable();
}

/**
 * Set GA as initialized (called after script loads and config is set)
 */
export function markGAAsInitialized(): void {
  isGAInitialized = true;
  if (import.meta.env.DEV) {
    console.log('[Analytics] GA marked as initialized');
  }
}

/**
 * Initialize Google Analytics 4
 * Called once when app mounts (by AnalyticsProvider)
 */
export function initGA(): void {
  if (typeof window === 'undefined') return;
  if (!GA_MEASUREMENT_ID) {
    console.error('[Analytics] GA_MEASUREMENT_ID not configured');
    return;
  }

  try {
    window.gtag('js', new Date());

    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      debug_mode: import.meta.env.DEV,
    });

    markGAAsInitialized();

    if (import.meta.env.DEV) {
      console.log('[Analytics] GA4 initialized with ID:', GA_MEASUREMENT_ID);
      console.log('[Analytics] DataLayer:', window.dataLayer);
    }
  } catch (error) {
    console.error('[Analytics] Failed to initialize GA4:', error);
  }
}

/**
 * Track a page view
 * Called automatically by AnalyticsProvider on route changes
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (!GA_MEASUREMENT_ID) {
    console.warn('[Analytics] ⚠️ GA_MEASUREMENT_ID not configured, page_view lost');
    return;
  }

  if (!isGAReady()) {
    console.warn('[Analytics] ⚠️ GA not ready yet, page_view lost:', pagePath);
    return;
  }

  try {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle || document.title,
    });

    if (import.meta.env.DEV) {
      console.log('[Analytics] ✓ page_view:', pagePath);
    }
  } catch (error) {
    console.error('[Analytics] Error tracking page_view:', error);
  }
}

/**
 * Track a custom event with type-safe parameters
 *
 * @example
 * trackEvent('share_click', { memory_slug: 'abc123', method: 'native' });
 */
export function trackEvent<T extends AnalyticsEventName>(
  eventName: T,
  params: T extends keyof AnalyticsEventParams ? AnalyticsEventParams[T] : Record<string, unknown>
): void {
  if (!GA_MEASUREMENT_ID) {
    console.warn('[Analytics] ⚠️ GA_MEASUREMENT_ID not configured, event lost:', eventName, params);
    return;
  }

  if (!isGAReady()) {
    console.warn('[Analytics] ⚠️ GA not ready yet, event lost:', eventName, params);
    return;
  }

  try {
    window.gtag('event', eventName, params);

    if (import.meta.env.DEV) {
      console.log('[Analytics] ✓ event:', eventName, params);
      console.log('[Analytics] DataLayer last entry:', window.dataLayer[window.dataLayer.length - 1]);
    }
  } catch (error) {
    console.error('[Analytics] Error tracking event:', eventName, error);
  }
}

/**
 * Track an error event
 * Useful for catching and reporting errors in the funnel
 */
export function trackError(
  context: string,
  error: Error | string,
  additionalParams?: Record<string, unknown>
): void {
  if (!isGAReady()) {
    console.warn('[Analytics] GA not ready, error event lost:', context);
    return;
  }

  const errorMessage = error instanceof Error ? error.message : error;

  try {
    window.gtag('event', 'exception', {
      description: `${context}: ${errorMessage}`,
      fatal: false,
      ...additionalParams,
    });

    if (import.meta.env.DEV) {
      console.log('[Analytics] error:', context, errorMessage);
    }
  } catch (err) {
    console.error('[Analytics] Error tracking error event:', err);
  }
}

/**
 * Set user properties (for user-scoped dimensions)
 *
 * @example
 * setUserProperties({ restaurant_id: 'acme' });
 */
export function setUserProperties(properties: Record<string, string | number | boolean>): void {
  if (!isGAReady()) {
    console.warn('[Analytics] GA not ready, user_properties lost:', properties);
    return;
  }

  try {
    window.gtag('set', 'user_properties', properties);

    if (import.meta.env.DEV) {
      console.log('[Analytics] user_properties:', properties);
    }
  } catch (error) {
    console.error('[Analytics] Error setting user_properties:', error);
  }
}
